import React, { useCallback, useRef, useState } from "react";
import { Tooltip, useRestoreFocusTarget } from "@fluentui/react-components";

import { AppMenuFlyout } from "./Flyouts/AppMenuFlyout";
import { IAppMenuIconButtonProps } from "./AppMenuIconButton.types";
import { observer } from "mobx-react";
import { useStyles } from "./AppMenuIconButton.styles";

const AppMenuIconButtonBase = (props: IAppMenuIconButtonProps) => {
    const { item } = props;
    const classNames = useStyles();
    const restoreFocusTargetAttribute = useRestoreFocusTarget();
    const buttonRef = useRef<HTMLDivElement | null>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    const onClickIcon = useCallback(
        (e?: React.MouseEvent | React.KeyboardEvent) => {
            if (item.onClick) {
                item.onClick(e, buttonRef.current);
            } else {
                setIsVisible(false);
                setIsOpen((prev) => !prev);
            }
        },
        [item]
    );

    const onDismiss = useCallback(() => {
        setIsOpen(false);
    }, []);

    const onMouseHover = useCallback(() => {
        setIsVisible(true);
    }, []);

    const onMouseOut = useCallback(() => {
        setIsVisible(false);
    }, []);

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === " " || e.key === "Enter") {
                onClickIcon(e);
            }
        },
        [onClickIcon]
    );

    const renderContent = useCallback(
        () => (
            <div>
                <div
                    title={item.displayName}
                    className={item.isHidden ? classNames.hiddenStyle : classNames.root}
                    onClick={onClickIcon}
                    onMouseOver={onMouseHover}
                    onFocus={onMouseHover}
                    onMouseOut={onMouseOut}
                    onBlur={onMouseOut}
                    role="button"
                    tabIndex={0}
                    onKeyDown={onKeyDown}
                    aria-label={item.displayName}
                    ref={buttonRef}
                    {...restoreFocusTargetAttribute}
                >
                    <item.icon />
                </div>
                {item.flyoutContent ? <AppMenuFlyout showFlyout={isOpen} onDismiss={onDismiss} menuItem={item} /> : null}
            </div>
        ),
        [item, classNames.hiddenStyle, classNames.root, onClickIcon, onMouseHover, onMouseOut, onKeyDown, restoreFocusTargetAttribute, isOpen, onDismiss]
    );

    return (
        <Tooltip content={item.displayName} relationship="inaccessible" positioning="below" visible={isVisible}>
            {renderContent()}
        </Tooltip>
    );
};

export const AppMenuIconButton = observer(AppMenuIconButtonBase);
